import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useAppStore } from '../../app/appStore';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { precheckOtpIdentifier, sendForgotPasswordOtp, sendRegisterOtp } from './api';
import { formatCountdown, loginOtpLockoutLabel, normalizeOtpExpiresAt } from './authShared';

// 'register' is Task 10's addition — see the module comment below for why it
// stayed a one-value type until now, and why its precheck direction is not a
// copy-paste of reset-password's.
export type OtpPurpose = 'reset_password' | 'register';

/**
 * The OTP send / cooldown / expiry / lockout state machine, pulled out of
 * `ForgotForm` (Task 9) so a later register-by-phone flow does not have to
 * copy it — a second copy of a 60s-cooldown-plus-10-minute-expiry timer is
 * exactly the kind of thing that drifts the moment one side gets a bugfix
 * the other doesn't.
 *
 * Two callers today: `ForgotForm` (`reset_password`) and `RegisterForm`
 * (`register`, Task 10). The existing login-by-OTP flow (still living in
 * `LoginForm`) is deliberately NOT folded in — it additionally tracks a
 * lockout and skips the precheck on resend, and this hook always prechecks,
 * the way `ForgotForm` always did.
 *
 * The two wired purposes ask the precheck the *opposite* question:
 * reset-password wants the identifier to already have an account
 * (`exists: true` proceeds; `false` shows "no account, want to register?").
 * Registration wants the opposite (`exists: false` proceeds; `true` shows
 * "already registered, want to log in?") — see `send` below. Getting this
 * backwards is exactly the bug the Task 9 review caught upstream (the old
 * hook's stub always called `precheckOtpIdentifier(id, 'login')`
 * regardless of purpose): a register precheck answered under the 'login'
 * question would tell a brand-new phone number "no account" and then
 * (correctly, but for the wrong reason) let it through — silently right for
 * the easy case, silently wrong the day the precheck endpoint actually
 * ships and starts answering `exists` for real.
 */
export function useOtpChallenge(purpose: OtpPurpose): {
  sentTo: string;
  cooldownLeft: number;
  expiryText: string;
  lockoutText: string;
  precheck: 'idle' | 'checking' | 'notfound' | 'taken';
  isPending: boolean;
  isError: boolean;
  error: unknown;
  send: (identifier: string) => Promise<void>;
  reset: () => void;
} {
  const t = useI18nStore((state) => state.t);
  const showToast = useAppStore((state) => state.showToast);

  const [sentTo, setSentTo] = useState('');
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [expiresAt, setExpiresAt] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [lockoutUntil, setLockoutUntil] = useState(0);
  const [lockoutReason, setLockoutReason] = useState('');
  const [precheck, setPrecheck] = useState<'idle' | 'checking' | 'notfound' | 'taken'>('idle');

  useEffect(() => {
    if (!expiresAt && !cooldownUntil && !lockoutUntil) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [cooldownUntil, expiresAt, lockoutUntil]);

  const sendMutation = useMutation({
    mutationFn: (identifier: string) => (
      purpose === 'register' ? sendRegisterOtp(identifier) : sendForgotPasswordOtp(identifier)
    ),
    onMutate: () => {
      showToast(t('authSendingOtp'), 'info');
    },
    onSuccess: (payload, identifier) => {
      setSentTo(identifier.trim());
      setExpiresAt(normalizeOtpExpiresAt(payload));
      setCooldownUntil(Date.now() + 60_000);
      setNow(Date.now());
      showToast(t('authOtpSent'), 'success');
    },
    onError: (error) => {
      // The precheck above is a courtesy skip, not the authority — a number
      // that slipped past it (unsupported purpose, or registered in the gap
      // between the two calls) still hits this from the real send-otp
      // endpoint. Route it to the same "already registered" UI instead of
      // the generic failure toast, so the player sees a login link instead
      // of "failed to send, try again" for an error that retrying can't fix.
      if (purpose === 'register' && error instanceof Error && error.message === 'PHONE_TAKEN') {
        setPrecheck('taken');
        return;
      }
      showToast(t('authOtpFail'), 'error');
    },
  });

  const cooldownLeft = Math.max(0, Math.ceil((cooldownUntil - now) / 1000));
  const otpSecondsLeft = Math.max(0, Math.ceil((expiresAt - now) / 1000));
  const lockoutText = loginOtpLockoutLabel(lockoutUntil, lockoutReason, t);
  const expiryText = otpSecondsLeft > 0 ? t('loginOtpExpiresIn').replace('%t%', formatCountdown(otpSecondsLeft)) : '';

  // Every send — first or resend — goes through the existence precheck first
  // (PM anti-abuse spec). Both callers' one send/resend button always calls
  // this; neither distinguishes first-send from resend the way the login-OTP
  // flow does.
  async function send(identifier: string) {
    if (sendMutation.isPending || cooldownLeft > 0) return;
    setPrecheck('checking');
    try {
      if (purpose === 'register') {
        // Registration wants the identifier to NOT already have an account —
        // the opposite of reset-password's question, and answered by the
        // same endpoint under a 'register' purpose bucket. Only a *positive*
        // answer blocks the send: an unsupported precheck (backend has not
        // shipped this purpose yet — 404/405/501, or a network error) must
        // not read as "taken", or no one could ever register while the
        // endpoint is down. The real send-otp call below is the backend's
        // authoritative PHONE_TAKEN check either way.
        const check = await precheckOtpIdentifier(identifier, 'register');
        if (check.supported && check.exists) {
          setPrecheck('taken');
          return;
        }
      } else {
        const check = await precheckOtpIdentifier(identifier, 'reset');
        if (!check.exists) {
          setPrecheck('notfound');
          return;
        }
      }
      setPrecheck('idle');
      sendMutation.mutate(identifier);
    } catch {
      setPrecheck('idle');
      showToast(t('authOtpFail'), 'error');
    }
  }

  function reset() {
    setSentTo('');
    setCooldownUntil(0);
    setExpiresAt(0);
    setLockoutUntil(0);
    setLockoutReason('');
    setPrecheck('idle');
    sendMutation.reset();
  }

  return {
    sentTo,
    cooldownLeft,
    expiryText,
    lockoutText,
    precheck,
    isPending: sendMutation.isPending,
    isError: sendMutation.isError,
    error: sendMutation.error,
    send,
    reset,
  };
}
